// ============================================================================
// design-fingerprint.js — "is this the same ARTWORK?", in 8 hex characters
// ============================================================================
// CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-07-31)
//
// ── WHY THIS EXISTS: THE R2 KEY WAS ONLY EVER SAFE BY ACCIDENT ──────────────────────
// `/wd-plate-upload` derives its object key server-side:
//
//     wd-plates/{designId}/{weight}{unit}-{colour}-{layout}.png
//
// Every dimension in that key is one a single SEND could vary — and until today one send
// read exactly ONE envelope (`ctx.shareEnvelope()` at the moment the dialog ran), so every
// row of a push shared a logo, a typeface and its wording. Only the keyed dimensions could
// differ, so the key was unique. That was never a property of the key; it was a property
// of there being one design in the room.
//
// The staging tray puts several designs in the room. Ryan 2026-07-30, describing the flow
// he actually wants: *"design a plate, go into the add to a project menu, add that plate…
// change the plate color in the editor, go back… add it, repeat."* Swap the LOGO between
// two of those primes at the same weight/colour/layout and the second upload silently
// overwrites the first — same key, one object — and the packet then shows one plate twice
// with two different captions. That is not a corner case; it is the normal way a rep will
// use a tray ("here's the crest version, here's the wordmark version").
//
// So the key gains an optional trailing segment, and this module mints it:
//
//     wd-plates/{designId}/{weight}{unit}-{colour}-{layout}-{envHash}.png
//
// ⚠ OPTIONAL IS LOAD-BEARING. Ryan already has real plates in R2 from 2026-07-30's testing.
// A tile carrying no `envHash` must derive the byte-identical key it derives today, or every
// plate pushed before this change 404s out of a packet that used to generate. Never send
// `envHash` on a path that didn't compute one, and never send it on the upload without also
// putting it on the tile (or vice-versa) — the two halves must agree or the packet resolves
// nothing.
//
// ── THE NORMALISATION IS THE WHOLE IDEA, NOT A DETAIL ────────────────────────────────
// Four fields are removed from the snapshot before hashing — `bodyColor`, `layoutPreset`,
// `weightValue`, `unit` — because those four are ALREADY IN THE KEY. Hashing them again
// would be double-counting, and it would cost the thing that makes the tray cheap:
//
//   • As the key's uniqueness test, it answers "different artwork?" rather than "different
//     plate?", which is exactly the axis the rest of the key doesn't cover.
//   • As the TRAY's identity, it means a colour-only sweep — four primes of one design in
//     four school colours — yields the SAME fingerprint four times. `ui/project-tray.js`
//     stores one envelope per fingerprint, so that sweep is ONE multi-megabyte base plus
//     four tiny items instead of four bases and a blown localStorage quota.
//
// That second half only works because dyeing the plate is a pure field set. `ui/state.js`
// (setBodyColor, and the note under it) is explicit: *"plate-dye text/number contrast is
// resolved at RENDER time as a reversible, non-cumulative view … NOT by mutating the design
// here."* If that ever changes and a dye starts repainting elements, a colour sweep will
// legitimately produce several fingerprints — more bases, more storage, still CORRECT. The
// saving is a best case; the correctness is not conditional on it.
//
// A LAYOUT change is the other way round and worth stating plainly: `applyLayoutPreset`
// rebuilds `elements`, so changing layout changes the fingerprint even though `layoutPreset`
// itself is normalised out. That is right — a re-slotted plate IS different artwork — and it
// is harmless, because the layout is in the key on its own account too.
//
// ── TWO TRAPS THIS MODULE EXISTS TO AVOID ────────────────────────────────────────────
// ⚠ NEVER FINGERPRINT THE ENVELOPE STRING. `toAutosave(state)` (ui/state.js:234) embeds
//   `ts: Date.now()`, so two calls one millisecond apart produce two different strings for
//   an identical design. Hash the parsed `design` SNAPSHOT — the `{ profile }` object — and
//   nothing else on the envelope. (`picks`, `product` and `palette` also ride the envelope
//   and are UI state, not artwork; they are not part of the snapshot and so never reach here.)
//
// ⚠ NEVER TRUST KEY ORDER. `JSON.stringify` emits own keys in insertion order, and a snapshot
//   that has been through `JSON.parse` → `applySnapshot` → `serializeProfile` can come back
//   with its keys in a different order than the one that went in. Same design, different
//   string, different hash, and the tray would think a resumed plate was new art. The walk
//   below SORTS keys at every level, which is the only thing that makes this stable across a
//   round-trip.
// ============================================================================

/**
 * The four fields the R2 key already carries. Removed before hashing — see the header.
 * If the worker's `wdPlateKey` ever adds or drops a dimension, this set moves with it.
 */
export const KEYED_FIELDS = Object.freeze(['bodyColor', 'layoutPreset', 'weightValue', 'unit']);

/** The shape the worker validates against: exactly 8 lowercase hex characters. */
export const ENV_HASH_RE = /^[a-f0-9]{8}$/;

/**
 * Deterministic JSON — object keys sorted at every depth, arrays left in order.
 *
 * Arrays are NOT sorted, deliberately: `elements` is an ordered list and its order is part
 * of the design (paint order, and which piece the panel calls "first"). Two designs whose
 * elements are the same set in a different order are genuinely different plates.
 *
 * `undefined` is dropped exactly as `JSON.stringify` would drop it, so a field that is
 * absent and a field that is explicitly `undefined` hash alike — they render alike.
 */
export function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value === undefined ? null : value);
  if (Array.isArray(value)) return `[${value.map((v) => stableStringify(v)).join(',')}]`;
  const keys = Object.keys(value).filter((k) => value[k] !== undefined).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

/**
 * Strip the already-keyed fields off a design snapshot.
 *
 * Tolerant about what it is handed: the envelope's `design` is `{ profile: {...} }`
 * (ui/state.js `designSnapshot`), but a caller holding a bare serialized profile gets the
 * same answer. Stripping is done at the PROFILE level only — not at any depth — because a
 * blanket key-name strip would be guessing about nested shapes it has no business knowing.
 *
 * @param {object} snapshot the parsed `design` snapshot, or a serialized profile
 * @returns {object} a new object; the input is never mutated
 */
export function normaliseForFingerprint(snapshot) {
  const snap = (snapshot && typeof snapshot === 'object') ? snapshot : {};
  const profile = (snap.profile && typeof snap.profile === 'object') ? snap.profile : snap;
  const out = {};
  for (const key of Object.keys(profile)) {
    if (KEYED_FIELDS.includes(key)) continue;
    out[key] = profile[key];
  }
  return out;
}

/** WebCrypto, wherever we are. Present natively in every browser this ships to and in Node 18+. */
function subtleCrypto() {
  const c = globalThis.crypto;
  if (!c || !c.subtle) throw new Error('[design-fingerprint] no crypto.subtle in this environment');
  return c.subtle;
}

/**
 * The design's artwork fingerprint: SHA-256 of the normalised snapshot, first 8 hex chars.
 *
 * 8 hex = 32 bits = ~4.3 billion values. The birthday bound puts a 1-in-a-million collision
 * at roughly 93 distinct artworks IN ONE DESIGN'S NAMESPACE — the hash only has to be unique
 * inside `wd-plates/{designId}/`, not globally — and a rep stages a handful of plates, not
 * ninety. 8 was chosen over 12 because this string ends up in an object key a human reads
 * when debugging a packet, and the readability is worth more here than the extra 16 bits.
 *
 * @param {object} designSnapshot the PARSED `design` snapshot — never the envelope string
 * @returns {Promise<string>} 8 lowercase hex characters, matching ENV_HASH_RE
 */
export async function designFingerprint(designSnapshot) {
  const json = stableStringify(normaliseForFingerprint(designSnapshot));
  const bytes = new TextEncoder().encode(json);
  const digest = await subtleCrypto().digest('SHA-256', bytes);
  const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return hex.slice(0, 8);
}

/**
 * The same answer, starting from an autosave ENVELOPE string.
 *
 * Parses and hands the `design` member on — the header's first trap in one function, so no
 * caller has to remember that the envelope's own `ts` makes the string unusable. Returns
 * `null` rather than throwing when the envelope is missing or unparseable: a prime that
 * cannot be fingerprinted must still be able to fall back to the un-hashed key, which is the
 * behaviour every plate had before today.
 *
 * @param {string} envelope `ctx.shareEnvelope()` / `toAutosave(state)`
 * @returns {Promise<string|null>}
 */
export async function fingerprintEnvelope(envelope) {
  if (typeof envelope !== 'string' || !envelope) return null;
  let data = null;
  try { data = JSON.parse(envelope); } catch { return null; }
  if (!data || !data.design) return null;
  try { return await designFingerprint(data.design); } catch { return null; }
}
